import { Controller } from "@hotwired/stimulus"
import { pdpCommonProps } from "@/lib/pdpAnalytics"

const SURVEY_NAME = "Room photo realism"
const MAX_PHOTO_SIDE = 2048
const PHOTO_QUALITY = 0.85
const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 120000
const STEPS = ["picker", "progress", "result", "failure"]
const DELETE_GRACE_MS = 7 * 24 * 60 * 60 * 1000
const FAILURE_MESSAGES = {
  capped: "We've hit today's limit for wall previews. Please try again tomorrow.",
  default: "Something went wrong while placing the artwork. Please try again later.",
}

export default class extends Controller {
  static targets = [
    "sheet", "picker",
    "progress", "progressImage", "progressBar",
    "result", "resultImage", "deleteRequest", "deleteNotice", "failure", "failureMessage",
  ]

  static values = {
    generatorUri: String,
    listingId: String,
    artworkUri: String,
    widthCm: Number,
    heightCm: Number,
    userId: String,
    uid: String,
  }

  connect() {
    this.capture("room_photo_button_shown")
  }

  disconnect() {
    clearTimeout(this.pollTimer)
  }

  open() {
    const saved = this.savedJob
    this.capture("room_photo_modal_opened", { has_saved_result: Boolean(saved?.done) })
    this.dispatch("show-alert-modal", { prefix: false, detail: "room_photos_modal" })
    if (this.polling || this.shownResult) return

    if (saved && this.deletionExpired(saved.id)) this.forgetJob(saved.id)
    else if (saved?.done) this.succeed(saved.id, { restored: true })
    else if (saved) this.poll(saved.id, { restored: true })
    else this.show("picker")
  }

  async pick(event) {
    const file = event.target.files[0]
    event.target.value = ""
    if (!file) return

    this.capture("room_photo_selected", { file_type: file.type, file_size: file.size })
    this.showProgress(URL.createObjectURL(file))

    try {
      const { roomId } = await this.request("/api/room", {
        method: "POST",
        body: { room: await this.encode(file), uid: this.uidValue, userId: this.userIdValue },
      })
      this.capture("room_photo_uploaded")
      this.generate(roomId)
    } catch (error) {
      this.fail("upload_failed", error.message)
    }
  }

  addToCart() {
    this.capture("room_photo_add_to_cart")
    this.close()
  }

  close() {
    this.element.querySelector("[aria-label='Close']").click()
  }

  async generate(roomId) {
    try {
      const { jobId, existing } = await this.request("/api/generate", {
        method: "POST",
        body: {
          roomId,
          artworkUrl: this.artworkUriValue,
          listingUrl: location.origin + location.pathname,
          widthCm: this.widthCmValue,
          heightCm: this.heightCmValue,
          listingId: this.listingIdValue,
          uid: this.uidValue,
          userId: this.userIdValue,
        },
      })
      this.savedJob = { id: jobId, done: false }
      this.poll(jobId, { restored: Boolean(existing) })
    } catch (error) {
      this.fail("generate_failed", error.message)
    }
  }

  poll(jobId, { restored }) {
    this.polling = true
    if (!this.progressTarget.classList.contains("hidden")) this.startProgressBar()
    else this.showProgress(null)
    const startedAt = Date.now()

    const check = async () => {
      if (!this.polling) return
      try {
        const { status, reason } = await this.request(`/api/result?id=${encodeURIComponent(jobId)}`)
        if (status === "done") return this.succeed(jobId, { restored, durationMs: Date.now() - startedAt })
        if (status !== "pending") return this.fail(status === "capped" ? "capped" : "generation_failed", reason)
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) return this.fail("timeout")
        this.pollTimer = setTimeout(check, POLL_INTERVAL_MS)
      } catch (error) {
        this.fail("poll_failed", error.message)
      }
    }
    check()
  }

  succeed(jobId, { restored, durationMs }) {
    this.polling = false
    this.shownResult = true
    this.savedJob = { id: jobId, done: true }
    this.jobId = jobId
    this.showDeleteRequested(Boolean(this.storage("get", this.deleteKeyFor(jobId))))
    this.resultImageTarget.src = `${this.generatorUriValue}/api/image?job=${encodeURIComponent(jobId)}`
    this.show("result")
    this.capture(restored ? "room_photo_result_restored" : "room_photo_generated", { duration_ms: durationMs })
    this.renderSurvey()
  }

  async requestDeletion() {
    try {
      await this.request("/api/delete-request", { method: "POST", body: { jobId: this.jobId } })
      this.storage("set", this.deleteKeyFor(this.jobId), String(Date.now()))
      this.showDeleteRequested(true)
      this.capture("room_photo_delete_requested")
    } catch (error) {
      this.capture("room_photo_delete_request_failed", { detail: error.message })
    }
  }

  showDeleteRequested(requested) {
    this.deleteRequestTarget.classList.toggle("hidden", requested)
    this.deleteNoticeTarget.classList.toggle("hidden", !requested)
  }

  fail(stage, detail) {
    this.polling = false
    this.storage("remove", this.jobKey)
    this.failureMessageTarget.textContent = FAILURE_MESSAGES[stage] || FAILURE_MESSAGES.default
    this.show("failure")
    this.capture("room_photo_generation_failed", { stage, detail })
  }

  showProgress(previewUri) {
    if (this.previewUri) URL.revokeObjectURL(this.previewUri)
    this.previewUri = previewUri
    if (previewUri) this.progressImageTarget.src = previewUri
    this.progressImageTarget.classList.toggle("hidden", !previewUri)
    this.show("progress")
    this.startProgressBar()
  }

  startProgressBar() {
    const bar = this.progressBarTarget
    bar.style.transition = "none"
    bar.style.width = "0"
    requestAnimationFrame(() => requestAnimationFrame(() => {
      bar.style.transition = ""
      bar.style.width = "90%"
    }))
  }

  show(step) {
    for (const name of STEPS) this[`${name}Target`].classList.toggle("hidden", name !== step)
    this.sheetTarget.dataset.step = step
  }

  async request(path, { method = "GET", body } = {}) {
    const send = () => fetch(`${this.generatorUriValue}${path}`, {
      method,
      headers: body ? { "content-type": "application/json" } : {},
      body: body ? JSON.stringify(body) : undefined,
    })
    const response = await send().catch(send) // a cold Netlify function can drop the first request
    const json = await response.json().catch(() => ({}))
    if (!response.ok) throw Object.assign(new Error(json.error || `HTTP ${response.status}`), { status: response.status })
    return json
  }

  async encode(file) {
    try {
      return await this.downscale(file)
    } catch {
      return this.readAsDataUrl(file) // browsers that can't decode HEIC send the original
    }
  }

  async downscale(file) {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" })
    const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    return canvas.toDataURL("image/jpeg", PHOTO_QUALITY)
  }

  readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })
  }

  renderSurvey() {
    window.posthog?.getActiveMatchingSurveys?.((surveys) => {
      const survey = surveys.find((candidate) => candidate.name === SURVEY_NAME)
      if (survey) window.posthog.renderSurvey(survey.id, "#room-photo-survey")
    }, true)
  }

  deleteKeyFor(jobId) {
    return `room-photo:delete-requested:${jobId}`
  }

  deletionExpired(jobId) {
    const requestedAt = Number(this.storage("get", this.deleteKeyFor(jobId)))
    return requestedAt > 0 && Date.now() - requestedAt > DELETE_GRACE_MS
  }

  forgetJob(jobId) {
    this.storage("remove", this.jobKey)
    this.storage("remove", this.deleteKeyFor(jobId))
    this.show("picker")
  }

  get jobKey() {
    return `room-photo:job:${this.listingIdValue}`
  }

  get savedJob() {
    try {
      return JSON.parse(this.storage("get", this.jobKey))
    } catch {
      return null
    }
  }

  set savedJob(job) {
    this.storage("set", this.jobKey, JSON.stringify(job))
  }

  storage(action, key, value) {
    try {
      return window.localStorage[`${action}Item`](key, value)
    } catch {
      return null
    }
  }

  capture(event, properties = {}) {
    const galleryData = document.getElementById("product-gallery")?.dataset || {}
    window.posthog?.capture(event, {
      ...pdpCommonProps(galleryData),
      user_id: this.userIdValue ? parseInt(this.userIdValue, 10) : null,
      uid: this.uidValue,
      ...properties,
    })
  }
}
